import { ReactNode } from 'react';
import { BoxesIcon, LayoutGridIcon } from 'lucide-react';

import { useEnvironment } from '@/react/portainer/environments/queries';
import {
  getDashboardRoute,
  getEnvironmentTypeIcon,
} from '@/react/portainer/environments/utils';
import { useEdgeGroup } from '@/react/edge/edge-groups/queries/useEdgeGroup';
import { useGroups } from '@/react/portainer/environments/environment-groups/queries';
import { addPlural } from '@/react/common/string-utils';

import { Icon } from '@@/Icon';
import { Link } from '@@/Link';

import { WorkflowStatus, WorkflowTarget, WorkflowType } from '../../types';

import { Block, Dot } from './Block';

export function TargetCell({
  target,
  type,
  status,
}: {
  target: WorkflowTarget | undefined;
  type: WorkflowType;
  status: WorkflowStatus;
}) {
  if (!target) {
    return null;
  }

  if (type === 'gitopsDeployment') {
    return <ReconciledTargets target={target} />;
  }

  if (type === 'edgeStack') {
    return (
      <div className="flex flex-col gap-1.5">
        {target.edgeGroupIds?.map((id) => (
          <EdgeGroupTarget
            key={id}
            id={id}
            status={target.groupStatus?.[id] ?? status}
          />
        ))}
      </div>
    );
  }

  return <StackTarget target={target} status={status} />;
}

/**
 * Summarises what a reconciled deployment is targeted at.
 *
 * What was picked rather than what it landed on: an environment reached through
 * an Edge group belongs to some environment group too, and rolling up by that
 * would list groups the user never chose. Each row carries the worst status
 * among the environments it reaches, so a fleet reads as a few rows rather than
 * one per environment; the status table below is where they are listed,
 * searched and paged.
 */
function ReconciledTargets({ target }: { target: WorkflowTarget }) {
  const environmentCount = target.resolvedEndpointIds?.length ?? 0;

  if (!environmentCount) {
    return <span className="text-gray-5">No target</span>;
  }

  return (
    <div className="flex flex-col gap-1.5">
      {target.edgeGroupIds?.map((id) => (
        <EdgeGroupTarget
          key={id}
          id={id}
          status={target.groupStatus?.[id] ?? 'unknown'}
        />
      ))}

      {target.environmentGroupIds?.map((id) => (
        <EnvironmentGroupTarget
          key={id}
          id={id}
          status={target.environmentGroupStatus?.[id] ?? 'unknown'}
        />
      ))}

      <span className="text-muted text-xs">
        {addPlural(environmentCount, 'environment')}
      </span>
    </div>
  );
}

function EnvironmentGroupTarget({
  id,
  status,
}: {
  id: number;
  status: WorkflowStatus;
}) {
  const { data: groups } = useGroups();
  const name = groups?.find((group) => group.Id === id)?.Name ?? `Group ${id}`;

  return (
    <TargetRow
      icon={
        <Icon icon={BoxesIcon} size="xs" className="shrink-0 text-gray-6" />
      }
      kind="Group"
      status={status}
    >
      <span className="tracking-wider text-gray-9 th-highcontrast:text-white th-dark:text-white">
        {name}
      </span>
    </TargetRow>
  );
}

function EdgeGroupTarget({
  id,
  status,
}: {
  id: number;
  status: WorkflowStatus;
}) {
  const { data } = useEdgeGroup(id);
  const name = data?.Name ?? `Edge Group ${id}`;
  return (
    <TargetRow
      icon={
        <Icon
          icon={LayoutGridIcon}
          size="xs"
          className="shrink-0 text-gray-6"
        />
      }
      kind="Edge group"
      status={status}
    >
      <Link
        to="edge.groups.edit"
        params={{ groupId: id }}
        data-cy={`workflow-edge-group-link-${id}`}
        className="tracking-wider text-gray-9 th-highcontrast:text-white th-dark:text-white"
      >
        {name}
      </Link>
    </TargetRow>
  );
}

function StackTarget({
  target,
  status,
}: {
  target: WorkflowTarget;
  status: WorkflowStatus;
}) {
  const { data: environment } = useEnvironment(target.endpointId);

  if (!target.endpointId) {
    return <span className="text-gray-5">No target</span>;
  }

  const envName = environment?.Name ?? `Environment ${target.endpointId}`;
  const icon = environment
    ? getEnvironmentTypeIcon(environment.Type)
    : 'circle';
  const label = target.namespace ? `${envName} / ${target.namespace}` : envName;
  const dashboardRoute = environment
    ? getDashboardRoute(environment)
    : { to: '', params: {} };

  return (
    <TargetRow
      icon={<Icon icon={icon} size="xs" className="shrink-0 text-gray-6" />}
      status={status}
    >
      <Link
        to={dashboardRoute.to}
        params={dashboardRoute.params}
        data-cy={`workflow-environment-link-${target.endpointId}`}
        className="tracking-wider text-gray-9 th-highcontrast:text-white th-dark:text-white"
      >
        {label}
      </Link>
    </TargetRow>
  );
}

function TargetRow({
  icon,
  kind,
  status,
  children,
}: {
  icon: ReactNode;
  /** Names which kind of group this is, where more than one kind can appear. */
  kind?: string;
  status: WorkflowStatus;
  children: ReactNode;
}) {
  return (
    <Block status={status} className="flex items-center gap-2">
      <Dot status={status} />
      {icon}
      {kind && (
        <span className="shrink-0 text-xs tracking-wide text-gray-6 uppercase th-highcontrast:text-gray-5 th-dark:text-gray-5">
          {kind}
        </span>
      )}
      {children}
    </Block>
  );
}
